// src/components/AdminService.js (Updated with Enhanced Bulk ID Card Generator support)
import axios from 'axios';

// Define API URL
const API_URL = process.env.REACT_APP_API_URL;

const adminService = {
  // Original methods from existing AdminService
  getAllUsers: async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      return response.data;
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  },

  getAllExports: async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/exports`);
      return response.data;
    } catch (error) {
      console.error('Error getting all exports:', error);
      throw error;
    }
  },

  updateUserRole: async (userId, role) => {
    try {
      const response = await axios.put(`${API_URL}/admin/users/${userId}/role`, { role });
      return response.data;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  },

  deleteUser: async (userId) => {
    try {
      const response = await axios.delete(`${API_URL}/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  // Original invite user method (kept for backward compatibility)
  inviteUser: async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/admin/users/invite`, userData);
      return response.data;
    } catch (error) {
      console.error('Error inviting user:', error);
      throw error;
    }
  },

  inviteUserEmail: async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/admin/users/invite-email`, userData);
      return response.data;
    } catch (error) {
      console.error('Error inviting user by email:', error);
      throw error;
    }
  },

  bulkInviteUsers: async (usersData) => {
    try {
      const response = await axios.post(`${API_URL}/admin/users/bulk-invite`, { users: usersData });
      return response.data;
    } catch (error) {
      console.error('Error bulk inviting users:', error);
      throw error;
    }
  },

  updateUserStatus: async (userId, status) => {
    try {
      const response = await axios.put(`${API_URL}/admin/users/${userId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  },

  getUserById: async (userId) => {
    try {
      // Get user data from PostgreSQL admin endpoint
      const adminResponse = await axios.get(`${API_URL}/admin/users/${userId}/profile`);
      const userData = adminResponse.data;
      
      // Also try to get avatar data from MongoDB profile endpoint
      try {
        const profileResponse = await axios.get(`${API_URL}/profiles/${userId}`);
        const profileData = profileResponse.data;
        
        // If MongoDB profile has avatar data, use it
        if (profileData.avatar_url || profileData.avatarUrl) {
          userData.avatar_url = profileData.avatar_url || profileData.avatarUrl;
          userData.avatar = profileData.avatar_url || profileData.avatarUrl;
          userData.has_avatar = profileData.has_avatar || false;
        }
      } catch (profileError) {
        // MongoDB profile not found or error - continue with PostgreSQL data
        console.log('MongoDB profile not found or error, using PostgreSQL data only:', profileError.message);
      }
      
      return userData;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  updateUserProfile: async (userId, profileData) => {
    try {
      const response = await axios.put(`${API_URL}/admin/users/${userId}/profile`, profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  // Enhanced ID Card Generation Methods
  generateBulkIdCards: async (userIds, options = {}) => {
    try {
      const response = await axios.post(`${API_URL}/admin/id-cards/bulk-generate`, {
        userIds,
        options
      });
      return response.data;
    } catch (error) {
      console.error('Error generating bulk ID cards:', error);
      throw error;
    }
  },

  // Enhanced method to save ID card metadata
  saveIdCardMetadata: async (metadata) => {
    try {
      const response = await axios.post(`${API_URL}/admin/id-cards/metadata`, metadata);
      return response.data;
    } catch (error) {
      console.error('Error saving ID card metadata:', error);
      throw error;
    }
  },

  // Get ID card exports with metadata
  getIdCardExports: async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/id-cards/exports`);
      return response.data;
    } catch (error) {
      console.error('Error getting ID card exports:', error);
      throw error;
    }
  },

  // Regenerate ID card from metadata
  regenerateIdCard: async (exportId) => {
    try {
      const response = await axios.post(`${API_URL}/admin/id-cards/regenerate/${exportId}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error regenerating ID card:', error);
      throw error;
    }
  },

  downloadIdCard: async (exportId) => {
    try {
      const response = await axios.get(`${API_URL}/admin/id-cards/download/${exportId}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading ID card:', error);
      throw error;
    }
  },

  deleteIdCardExport: async (exportId) => {
    try {
      const response = await axios.delete(`${API_URL}/admin/id-cards/exports/${exportId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting ID card export:', error);
      throw error;
    }
  },

  // Bulk upload users with photos
  bulkUploadUsersWithPhotos: async (usersData) => {
    try {
      const response = await axios.post(`${API_URL}/admin/users/bulk-upload`, { users: usersData });
      return response.data;
    } catch (error) {
      console.error('Error bulk uploading users with photos:', error);
      throw error;
    }
  },

  // Get all companies for the current user (based on their role)
  getUserCompanies: async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`);
      return response.data.companies || response.data;
    } catch (error) {
      console.error('Error getting user companies:', error);
      throw error;
    }
  },

  // Get a specific company with details
  getCompanyDetails: async (companyId) => {
    try {
      const response = await axios.get(`${API_URL}/api/companies/${companyId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting company details:', error);
      throw error;
    }
  },

  // Get users for a specific company
  getCompanyUsers: async (companyId) => {
    try {
      const response = await axios.get(`${API_URL}/api/companies/${companyId}/users`);
      return response.data;
    } catch (error) {
      console.error('Error getting company users:', error);
      throw error;
    }
  },

  // Update a company's details
  updateCompany: async (companyId, companyData) => {
    try {
      const response = await axios.put(`${API_URL}/api/companies/${companyId}`, companyData);
      return response.data;
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  },

  // Add a user to a company
  addCompanyUser: async (companyId, userData) => {
    try {
      const response = await axios.post(`${API_URL}/api/companies/${companyId}/users`, userData);
      return response.data;
    } catch (error) {
      console.error('Error adding user to company:', error);
      throw error;
    }
  },

  // Update a user's role within a company
  updateCompanyUserRole: async (companyId, userId, role) => {
    try {
      const response = await axios.put(`${API_URL}/api/companies/${companyId}/users/${userId}`, { role });
      return response.data;
    } catch (error) {
      console.error('Error updating user role in company:', error);
      throw error;
    }
  },

  // Remove a user from a company
  removeCompanyUser: async (companyId, userId) => {
    try {
      const response = await axios.delete(`${API_URL}/api/companies/${companyId}/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing user from company:', error);
      throw error;
    }
  },

  // Get company invoices
  getCompanyInvoices: async (companyId) => {
    try {
      const response = await axios.get(`${API_URL}/api/companies/${companyId}/invoices`);
      return response.data;
    } catch (error) {
      console.error('Error getting company invoices:', error);
      throw error;
    }
  },

  deleteCompany: async (companyId) => {
    try {
      const response = await axios.delete(`${API_URL}/api/admin/companies/${companyId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  },

  // Mock implementations for development without backend
  mockGetAllUsers: () => {
    // Return mock data for testing with more comprehensive user data
    return Promise.resolve([
      {
        id: '1',
        name: 'Admin User',
        email: 'admin@example.com',
        role: 'admin',
        profile_visibility: 'public',
        skills_count: 5,
        languages_count: 3,
        company: 'Finaran Consulting',
        title: 'Senior Administrator',
        designation: 'Senior Administrator',
        department: 'IT Management',
        phone: '+1 (555) 123-4567',
        location: 'New York, NY',
        avatar: '/api/placeholder/150/150',
        employeeId: 'EMP-0001',
        bloodGroup: 'A+',
        created_at: new Date().toISOString()
      },
      {
        id: '2',
        name: 'John Smith',
        email: 'john@finaran.com',
        role: 'user',
        profile_visibility: 'private',
        skills_count: 3,
        languages_count: 2,
        company: 'Finaran Consulting',
        title: 'Software Developer',
        designation: 'Senior Software Developer',
        department: 'Development',
        phone: '+1 (555) 234-5678',
        location: 'San Francisco, CA',
        avatar: '/api/placeholder/150/150',
        employeeId: 'EMP-0002',
        bloodGroup: 'B+',
        created_at: new Date().toISOString()
      },
      {
        id: '3',
        name: 'Sarah Johnson',
        email: 'sarah@finaran.com',
        role: 'user',
        profile_visibility: 'enabled',
        skills_count: 7,
        languages_count: 3,
        company: 'Finaran Consulting',
        title: 'Project Manager',
        designation: 'Senior Project Manager',
        department: 'Operations',
        phone: '+1 (555) 345-6789',
        location: 'Chicago, IL',
        avatar: '/api/placeholder/150/150',
        employeeId: 'EMP-0003',
        bloodGroup: 'O+',
        created_at: new Date().toISOString()
      },
      {
        id: '4',
        name: 'Mike Williams',
        email: 'mike@finaran.com',
        role: 'user',
        profile_visibility: 'enabled',
        skills_count: 4,
        languages_count: 1,
        company: 'Finaran Consulting',
        title: 'UI/UX Designer',
        designation: 'Lead UI/UX Designer',
        department: 'Design',
        phone: '+1 (555) 456-7890',
        location: 'Austin, TX',
        avatar: '/api/placeholder/150/150',
        employeeId: 'EMP-0004',
        bloodGroup: 'AB+',
        created_at: new Date().toISOString()
      },
      {
        id: '5',
        name: 'Emily Davis',
        email: 'emily@finaran.com',
        role: 'user',
        profile_visibility: 'enabled',
        skills_count: 6,
        languages_count: 2,
        company: 'Finaran Consulting',
        title: 'Data Analyst',
        designation: 'Senior Data Analyst',
        department: 'Analytics',
        phone: '+1 (555) 567-8901',
        location: 'Seattle, WA',
        avatar: '/api/placeholder/150/150',
        employeeId: 'EMP-0005',
        bloodGroup: 'O-',
        created_at: new Date().toISOString()
      },
      {
        id: '6',
        name: 'Alex Chen',
        email: 'alex@techcorp.com',
        role: 'user',
        profile_visibility: 'enabled',
        skills_count: 8,
        languages_count: 4,
        company: 'TechCorp Solutions',
        title: 'Full Stack Developer',
        designation: 'Senior Full Stack Developer',
        department: 'Engineering',
        phone: '+1 (555) 678-9012',
        location: 'San Jose, CA',
        avatar: '/api/placeholder/150/150',
        employeeId: 'EMP-0006',
        bloodGroup: 'A-',
        created_at: new Date().toISOString()
      },
      {
        id: '7',
        name: 'Maria Rodriguez',
        email: 'maria@techcorp.com',
        role: 'user',
        profile_visibility: 'enabled',
        skills_count: 5,
        languages_count: 3,
        company: 'TechCorp Solutions',
        title: 'Marketing Manager',
        designation: 'Senior Marketing Manager',
        department: 'Marketing',
        phone: '+1 (555) 789-0123',
        location: 'Los Angeles, CA',
        avatar: '/api/placeholder/150/150',
        employeeId: 'EMP-0007',
        bloodGroup: 'B-',
        created_at: new Date().toISOString()
      }
    ]);
  },

  mockBulkInviteUsers: (usersData) => {
    // Simulate some success and some failure
    const successCount = Math.floor(usersData.length * 0.9); // 90% success rate for mocking

    const processedUsers = usersData.slice(0, successCount).map((userData, index) => ({
      id: Math.floor(Math.random() * 1000 + 100).toString(),
      name: userData.name || '',
      email: userData.email,
      role: userData.role || 'user',
      invitation_sent: true,
      invitation_date: new Date().toISOString()
    }));

    const failedEmails = usersData.slice(successCount).map(user => user.email);

    return Promise.resolve({
      users: processedUsers,
      successCount: processedUsers.length,
      failedCount: failedEmails.length,
      failedEmails: failedEmails
    });
  },

  mockGetAllExports: () => {
    // Return mock data for testing
    return Promise.resolve([
      {
        id: '1',
        user_name: 'Admin User',
        user_email: 'admin@example.com',
        filename: 'admin-profile.html',
        format: 'html',
        created_at: new Date().toISOString()
      },
      {
        id: '2',
        user_name: 'John Smith',
        user_email: 'john@finaran.com',
        filename: 'john-profile.php',
        format: 'php',
        created_at: new Date().toISOString()
      }
    ]);
  },

  // Add a mock for the status update
  mockUpdateUserStatus: (userId, status) => {
    return Promise.resolve({
      id: userId,
      name: 'Mock User',
      email: 'mock@example.com',
      status: status,
      message: `User profile has been ${status}`
    });
  },

  mockGetUserById: (userId) => {
    // Generate mock data for the user
    return Promise.resolve({
      id: userId,
      name: 'Test User',
      email: 'testuser@example.com',
      role: 'user',
      profile_visibility: 'public',
      company: 'Example Corp',
      title: 'Software Developer',
      department: 'Engineering',
      bio: 'This is a test bio for the user.',
      phone: '123-456-7890',
      location: 'New York, NY',
      linkedin_url: 'https://linkedin.com/in/testuser',
      skills: ['JavaScript', 'React', 'Node.js'],
      languages: ['English', 'Spanish'],
      avatar: `/api/profiles/${userId}/avatar`, // Use actual avatar endpoint
      avatar_url: `/api/profiles/${userId}/avatar`,
      has_avatar: true,
      employeeId: `EMP-${String(userId).padStart(4, '0')}`,
      created_at: new Date().toISOString()
    });
  },

  // Add a mock implementation for development
  mockUpdateUserProfile: (userId, profileData) => {
    return Promise.resolve({
      message: 'Profile updated successfully',
      userId: userId
    });
  },

  mockInviteUserEmail: (userData) => {
    return Promise.resolve({
      id: Math.floor(Math.random() * 1000).toString(),
      name: userData.name || '',
      email: userData.email,
      role: userData.role || 'user',
      invitation_sent: true,
      invitation_date: new Date().toISOString()
    });
  },

  // Enhanced mock implementations for bulk ID card generation
  mockGenerateBulkIdCards: (userIds, options = {}) => {
    return Promise.resolve({
      exports: userIds.map(userId => ({
        id: `export_${Date.now()}_${userId}`,
        user_id: userId,
        filename: `user_${userId}_id_card.pdf`,
        format: 'pdf',
        type: 'id_card',
        file_size: Math.floor(Math.random() * 500000) + 100000, // Random size between 100KB-600KB
        created_at: new Date().toISOString(),
        options: options
      })),
      successCount: userIds.length,
      failedCount: 0,
      message: `Successfully generated ${userIds.length} ID cards`
    });
  },

  mockSaveIdCardMetadata: (metadata) => {
    return Promise.resolve({
      id: `metadata_${Date.now()}`,
      ...metadata,
      saved_at: new Date().toISOString(),
      message: 'ID card metadata saved successfully'
    });
  },

  mockGetIdCardExports: () => {
    // This would normally fetch from backend, but for mock we return empty
    // The actual ID card exports are stored in localStorage in the frontend
    return Promise.resolve([]);
  },

  mockRegenerateIdCard: (exportId) => {
    // Mock regeneration - in real implementation, this would recreate PDF from stored metadata
    const mockPdfData = 'Regenerated PDF data for ID card';
    const blob = new Blob([mockPdfData], { type: 'application/pdf' });
    return Promise.resolve(blob);
  },

  mockDownloadIdCard: (exportId) => {
    // Mock blob data for download
    const mockPdfData = 'Mock PDF data for ID card';
    const blob = new Blob([mockPdfData], { type: 'application/pdf' });
    return Promise.resolve(blob);
  },

  mockDeleteIdCardExport: (exportId) => {
    return Promise.resolve({
      message: 'ID card export deleted successfully',
      exportId: exportId
    });
  },

  mockBulkUploadUsersWithPhotos: (usersData) => {
    const processedUsers = usersData.map((userData, index) => ({
      id: Math.floor(Math.random() * 1000 + 100).toString(),
      ...userData,
      created_at: new Date().toISOString(),
      status: 'active'
    }));

    return Promise.resolve({
      users: processedUsers,
      successCount: processedUsers.length,
      failedCount: 0,
      message: `Successfully uploaded ${processedUsers.length} users with photos`
    });
  },

  // Mock company-related methods (existing)
  mockGetUserCompanies: () => {
    return Promise.resolve([
      {
        id: 1,
        name: 'Finaran Consulting',
        domain: 'finaran.com',
        email: 'info@finaran.com',
        status: 'active',
        planType: 'Professional',
        maxUsers: 25,
        user_count: 5,
        features: {
          enable_ai_signature: true,
          enable_signature_templates: true
        }
      },
      {
        id: 2,
        name: 'TechCorp Solutions',
        domain: 'techcorp.com',
        email: 'info@techcorp.com',
        status: 'active',
        planType: 'Enterprise',
        maxUsers: 100,
        user_count: 2,
        features: {
          enable_ai_signature: true,
          enable_signature_templates: true
        }
      }
    ]);
  },

  mockGetCompanyDetails: (companyId) => {
    const companies = {
      1: {
        id: companyId,
        name: 'Finaran Consulting',
        domain: 'finaran.com',
        email: 'info@finaran.com',
        phone: '+1 (555) 123-4567',
        address: '123 Business St, Suite 500',
        city: 'New York',
        state: 'NY',
        zip: '10001',
        country: 'USA',
        website: 'https://finaran.com',
        status: 'active',
        planType: 'Professional',
        maxUsers: 25,
        billingCycle: 'annually',
        features: {
          enable_ai_signature: true,
          enable_signature_templates: true
        },
        billing: {
          companyName: 'Finaran Consulting',
          contactName: 'Finance Department',
          contactEmail: 'billing@finaran.com',
          vatId: 'VAT12345678',
          billingAddress: '123 Business St, Suite 500, New York, NY 10001, USA',
          paymentMethod: 'invoice'
        },
        user_count: 5
      },
      2: {
        id: companyId,
        name: 'TechCorp Solutions',
        domain: 'techcorp.com',
        email: 'info@techcorp.com',
        phone: '+1 (555) 987-6543',
        address: '456 Tech Ave, Floor 12',
        city: 'San Francisco',
        state: 'CA',
        zip: '94105',
        country: 'USA',
        website: 'https://techcorp.com',
        status: 'active',
        planType: 'Enterprise',
        maxUsers: 100,
        billingCycle: 'annually',
        features: {
          enable_ai_signature: true,
          enable_signature_templates: true
        },
        billing: {
          companyName: 'TechCorp Solutions',
          contactName: 'Billing Team',
          contactEmail: 'billing@techcorp.com',
          vatId: 'VAT87654321',
          billingAddress: '456 Tech Ave, Floor 12, San Francisco, CA 94105, USA',
          paymentMethod: 'credit_card'
        },
        user_count: 2
      }
    };
    
    return Promise.resolve(companies[companyId] || companies[1]);
  },
  
  mockGetCompanyUsers: () => {
    return Promise.resolve([
      { id: 1, name: 'John Smith', email: 'john@finaran.com', company_role: 'admin', status: 'active', lastLogin: '2023-03-10T14:30:00Z' },
      { id: 2, name: 'Sarah Johnson', email: 'sarah@finaran.com', company_role: 'admin', status: 'active', lastLogin: '2023-03-12T09:15:00Z' },
      { id: 3, name: 'Mike Williams', email: 'mike@finaran.com', company_role: 'user', status: 'active', lastLogin: '2023-03-08T11:45:00Z' },
      { id: 4, name: 'Emily Davis', email: 'emily@finaran.com', company_role: 'user', status: 'active', lastLogin: '2023-03-08T11:45:00Z' },
      { id: 5, name: 'Office Account', email: 'office@finaran.com', company_role: 'admin', status: 'active', lastLogin: '2023-03-15T10:30:00Z' }
    ]);
  },
  
  mockGetCompanyInvoices: () => {
    return Promise.resolve([
      { 
        id: 'INV-2023-001', 
        date: '2023-01-15', 
        amount: 4800.00, 
        currency: 'USD', 
        status: 'paid', 
        description: 'Annual subscription - Pro Plan (25 users)'
      },
      { 
        id: 'INV-2023-002', 
        date: '2023-02-15', 
        amount: 300.00, 
        currency: 'USD', 
        status: 'paid', 
        description: 'Additional user licenses (5 users)'
      },
      { 
        id: 'INV-2023-003', 
        date: '2023-03-15', 
        amount: 5200.00, 
        currency: 'USD', 
        status: 'pending', 
        description: 'Annual subscription renewal - Pro Plan (27 users + add-ons)'
      }
    ]);
  }
};

// If in development with no backend API, use mock data
if (process.env.REACT_APP_USE_MOCK_DATA === 'true') {
  adminService.getAllUsers = adminService.mockGetAllUsers;
  adminService.getAllExports = adminService.mockGetAllExports;
  adminService.inviteUserEmail = adminService.mockInviteUserEmail;
  adminService.bulkInviteUsers = adminService.mockBulkInviteUsers;
  adminService.updateUserStatus = adminService.mockUpdateUserStatus;
  adminService.getUserById = adminService.mockGetUserById;
  adminService.updateUserProfile = adminService.mockUpdateUserProfile;
  
  // Mock ID card-related methods
  adminService.generateBulkIdCards = adminService.mockGenerateBulkIdCards;
  adminService.saveIdCardMetadata = adminService.mockSaveIdCardMetadata;
  adminService.getIdCardExports = adminService.mockGetIdCardExports;
  adminService.regenerateIdCard = adminService.mockRegenerateIdCard;
  adminService.downloadIdCard = adminService.mockDownloadIdCard;
  adminService.deleteIdCardExport = adminService.mockDeleteIdCardExport;
  adminService.bulkUploadUsersWithPhotos = adminService.mockBulkUploadUsersWithPhotos;
  
  // Mock company-related methods
  adminService.getUserCompanies = adminService.mockGetUserCompanies;
  adminService.getCompanyDetails = adminService.mockGetCompanyDetails;
  adminService.getCompanyUsers = adminService.mockGetCompanyUsers;
  adminService.getCompanyInvoices = adminService.mockGetCompanyInvoices;
}

export default adminService;